import {
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  IconButton,
  makeStyles
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import CardTitle from "@saleor/components/CardTitle";
import Hr from "@saleor/components/Hr";
import { FormChange } from "@saleor/hooks/useForm";
import useNotifier from "@saleor/hooks/useNotifier";
import ImageIcon from "@saleor/icons/Image";
import { buttonMessages } from "@saleor/intl";
import classNames from "classnames";
import React from "react";
import Dropzone from "react-dropzone";
import { FormattedMessage, useIntl } from "react-intl";

import { CollectionDetailsPageFormData } from "../CollectionDetailsPage";
import CollectionPreviewDialog from "../CollectionPreviewDialog";

interface CollectionHeaderProps {
  data: CollectionDetailsPageFormData;
  loading?: boolean;
  onChange?: FormChange;
  onIconUpload?: (file: File) => void;
}

const CollectionHeader: React.FC<CollectionHeaderProps> = props => {
  const { data, loading, onChange, onIconUpload } = props;

  const useStyles = makeStyles(
    theme => ({
      buttonBase: {
        "&:hover": {
          alignItems: "center",
          borderRadius: "50%",
          display: "flex",
          justifyContent: "center"
        },
        minWidth: "52px !important"
      },
      buttonColor: {
        borderRadius: `50%`,
        display: "inline-block",
        height: "25px",
        width: "25px"
      },
      buttonSelected: {
        alignItems: "center",
        border: "3.5px solid #96AAFF",
        borderRadius: "50%",
        display: "flex",
        height: "37px",
        justifyContent: "center",
        width: "38px"
      },

      buttonSet: {
        display: "flex",
        justifyContent: "space-between",
        paddingLeft: "10px !important",
        paddingRight: "10px !important"
      },
      buttonUnselected: {
        alignItems: "center",
        border: "3.5px solid transparent",
        borderRadius: "50%",
        display: "flex",
        height: "37px",
        justifyContent: "center",
        width: "38px"
      },
      card: {
        marginTop: theme.spacing(2),
        [theme.breakpoints.down("sm")]: {
          marginTop: 0
        }
      },
      descriptionLabel: {
        margin: "5px 0px 5px 0px"
      },
      fileField: {
        display: "none"
      },
      fileUploadField: {
        padding: "0!important"
      },
      helpText: {
        gridColumnEnd: "span 4"
      },
      icon: {
        color: "rgba(255, 255, 255, 0.54)"
      },
      image: {
        height: "100%",
        objectFit: "contain",
        userSelect: "none",
        width: "100%"
      },
      imageContainer: {
        "&:hover, &.dragged": {
          "& $imageOverlay": {
            display: "block"
          }
        },
        background: "#ffffff",
        border: "1px solid #eaeaea",
        borderRadius: theme.spacing(),
        height: 140,
        margin: "auto",
        overflow: "hidden",
        // padding: theme.spacing(2),
        position: "relative",
        width: 140
      },
      imageGridContainer: {
        position: "relative"
      },
      imageOverlay: {
        background: "rgba(0, 0, 0, 0.6)",
        cursor: "move",
        display: "none",
        height: "100%",
        left: 0,
        // padding: theme.spacing(2),
        position: "absolute",
        top: 0,
        width: "100%"
      },
      imageOverlayShow: {
        "&$imageOverlay": {
          alignItems: "center",
          display: "flex",
          justifyContent: "center"
        }
      },
      imageOverlayToolbar: {
        display: "flex",
        // left: "95%",
        position: "absolute",
        right: 0,
        top: 0
      },

      imageUpload: {
        height: "100%",
        left: 0,
        outline: 0,
        position: "absolute",
        top: 0,
        width: "100%"
      },
      imageUploadActive: {
        zIndex: 1
      },
      imageUploadIconActive: {
        display: "block"
      },
      imageflex: {
        display: "flex"
      },
      lowerToolbar: {
        alignItems: "flex-end",
        display: "flex",
        paddingBottom: "15px",
        paddingRight: "20px"
      },
      photosIcon: {
        height: "64px",
        margin: "0 auto",
        width: "64px"
      },
      root: {
        display: "grid",
        gridColumnGap: theme.spacing(2),
        gridRowGap: theme.spacing(2),
        gridTemplateColumns: "repeat(4, 1fr)",
        [theme.breakpoints.down("sm")]: {
          gridTemplateColumns: "repeat(3, 1fr)"
        },
        [theme.breakpoints.down("xs")]: {
          gridTemplateColumns: "repeat(2, 1fr)"
        }
      },
      rootDragActive: {
        opacity: 0.2
      },
      uploadButton: {
        display: "block"
      },
      uploadRoot: {
        fontSize: 12,
        fontWeight: 600,
        padding: "15px",
        textAlign: "center"
      }
    }),
    { name: "CollectionHeader" }
  );

  const notify = useNotifier();
  const classes = useStyles(props);

  const intl = useIntl();
  const upload = React.useRef(null);
  const [iconToUpload, setIconToUpload] = React.useState(null);
  const [iconImage, setIconImage] = React.useState(null);
  const [selectedColor, setSelectedColor] = React.useState(null);
  const [previewModal, setPreviewModal] = React.useState(false);

  const handleChange = e => {
    setSelectedColor(e);
    onChange({
      target: {
        name: "color",
        value: e
      }
    });
  };

  const colorset = [
    "#0030FF",
    "#FFDB00",
    "#96AAFF",
    "#FF96A9",
    "#B55D6A",
    "#FA9E4A",
    "#7BDD85",
    "#91C7E8",
    "#B896FF",
    "#577CDB",
    "#59DCE4",
    "#F29B9B",
    "#3C5BCD",
    "#98DAD0"
  ];

  React.useEffect(() => {
    if (iconToUpload) {
      setIconImage(URL.createObjectURL(iconToUpload[0]));
      onIconUpload(iconToUpload[0]);
    } else {
      onIconUpload(null);
    }
  }, [iconToUpload]);

  React.useEffect(() => {
    if (data?.color) {
      setSelectedColor(data.color);
    } else {
      setSelectedColor("#0030FF");
    }
  }, [data]);

  const checkFile = e => {
    const file = e[0];
    if (
      file.type.includes("image/jpeg") ||
      file.type.includes("image/jpg") ||
      file.type.includes("image/png")
    ) {
      setIconToUpload(e);
      onIconUpload(e);
      onChange({
        target: {
          name: "iconImage",
          value: file
        }
      });
    } else {
      notify({
        status: "error",
        text: "File not supported"
      });
    }
  };

  return (
    <Card className={classes.card}>
      <CardTitle
        title={intl.formatMessage({
          defaultMessage: "Header Background Color",
          description: "section header"
        })}
      />
      <CardContent className={classes.buttonSet}>
        {colorset.map((color, index) => (
          <Button
            key={index}
            className={classes.buttonBase}
            onClick={() => handleChange(color)}
          >
            <div
              className={
                selectedColor === color
                  ? classes.buttonSelected
                  : classes.buttonUnselected
              }
            >
              <div
                className={classes.buttonColor}
                style={{
                  backgroundColor: `${color}`
                }}
              ></div>
            </div>
          </Button>
        ))}
      </CardContent>
      <Hr />
      <CardTitle
        title={intl.formatMessage({
          defaultMessage: "Header Icon",
          description: "section header"
        })}
      />
      <div
        className={classNames(classes.imageGridContainer, {
          [classes.imageflex]: iconToUpload || data.iconImage?.url
        })}
      >
        <CardContent>
          <Dropzone onDrop={checkFile} disableClick={true}>
            {({ getRootProps, getInputProps }) => (
              <div {...getRootProps()}>
                <CardContent
                  className={classes.fileUploadField}
                  // onClick={() => upload.current.click()}
                >
                  <input
                    {...getInputProps()}
                    id="fileUpload"
                    accept="image/jpeg, image/jpg, image/png"
                    onChange={e =>
                      e.target.files.length > 0 && checkFile(e.target.files)
                    }
                    multiple={false}
                    ref={upload}
                    type="file"
                  />
                  {iconToUpload || data.iconImage?.url ? (
                    <div className={classes.imageContainer}>
                      <div
                        className={classNames(classes.imageOverlay, {
                          [classes.imageOverlayShow]: loading
                        })}
                      >
                        {loading ? (
                          <CircularProgress size={32} />
                        ) : (
                          <div className={classes.imageOverlayToolbar}>
                            <IconButton
                              color="primary"
                              onClick={() => {
                                upload.current.value = null;
                                setIconImage(null);
                                setIconToUpload(null);
                                onChange({
                                  target: {
                                    name: "iconImage",
                                    value: null
                                  }
                                });
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </div>
                        )}
                      </div>
                      <img
                        className={classes.image}
                        src={iconToUpload ? iconImage : data.iconImage.url}
                      />
                    </div>
                  ) : (
                    <div className={classes.uploadRoot}>
                      <ImageIcon className={classes.photosIcon} />
                      <p className={classes.descriptionLabel}>
                        <FormattedMessage
                          defaultMessage="DROP ICON HERE TO UPLOAD"
                          description="description"
                        />
                      </p>
                    </div>
                  )}
                </CardContent>
              </div>
            )}
          </Dropzone>
        </CardContent>
      </div>
      <CardActions className={classes.lowerToolbar}>
        <>
          <Button
            onClick={() => setPreviewModal(!previewModal)}
            disabled={loading}
            variant="text"
            color="primary"
            data-test="button-upload-image"
            className={classes.uploadButton}
          >
            <FormattedMessage
              defaultMessage="Preview"
              description="preview modal toggle"
            />
          </Button>
          <CollectionPreviewDialog
            data={data}
            backgroundColor={selectedColor}
            open={previewModal}
            iconImage={iconImage}
            onClose={() => setPreviewModal(false)}
          />
          <Button
            onClick={() => upload.current.click()}
            disabled={loading}
            variant="text"
            color="primary"
            data-test="button-upload-image"
            className={classes.uploadButton}
          >
            {intl.formatMessage(buttonMessages.upload)}
          </Button>
          <input
            className={classes.fileField}
            id="fileUpload"
            onChange={event => checkFile(event.target.files)}
            type="file"
            ref={upload}
            accept="image/jpeg, image/jpg, image/png"
          />
        </>
      </CardActions>
    </Card>
  );
};
CollectionHeader.displayName = "CollectionHeader";
export default CollectionHeader;
